import { asRecord } from '@payaca/utilities/objectUtilities';
import { z } from 'zod/v4';
import { type SolarSavingsRegion } from './solarSavingsConstants';

/**
 * How Glow lays arrays — one object for YourPower's stream and our packer.
 *
 * Names are installer language, not OpenAPI. `edgeSetbackM` is the fire-break
 * number; eave/hip insets are derived so a 0.4 m setback matches today's
 * 0.4 / 0.25 / 0.2 packer defaults.
 */
export const NORTH_FACING_AZIMUTH_WINDOW_DEG = {
  min: 90,
  max: 270,
} as const;

/**
 * US edge setback: 18 in, the ridge clearance IFC 605.11.3 and NFPA 1 ask for
 * and the figure most AHJs adopt. The 0.4 m default below is an MCS-era UK
 * convention and is too tight to quote against a US fire code.
 */
export const US_DEFAULT_EDGE_SETBACK_M = 0.457;

export const DEFAULT_SOLAR_PLACEMENT_RULES = {
  edgeSetbackM: 0.4,
  panelGapM: 0.02,
  columnGapM: 0.02,
  rowGapM: 0.02,
  excludeNorthFacing: false,
  roofBearingWindowDeg: NORTH_FACING_AZIMUTH_WINDOW_DEG,
  singleIsland: true,
  singleOrientation: true,
  quadrilateralArrays: false,
  flatRoof: {
    // A pitched tile roof (typically 12.5°+) stays flush by default; frames
    // are for flat and near-flat roofs.
    maxSlopeDeg: 10,
    frameTiltDeg: 15,
    rowSpacingMultiplier: 1.5,
    rowSpacingMode: 'calculated',
  },
  minPanelsPerRoof: 0,
  minSlopeDeg: 0,
  minSunAccessPercent: 0,
} as const;

export const flatRoofSchema = z
  .object({
    maxSlopeDeg: z.number().min(0).max(45),
    frameTiltDeg: z.number().min(0).max(45),
    rowSpacingMultiplier: z.number().min(1).max(5),
    rowSpacingMode: z.enum(['calculated', 'fixed']),
    /** Edge-to-edge distance projected onto the physical roof, not centre pitch. */
    fixedClearGapM: z.number().min(0).max(10).optional(),
  })
  .refine(
    (value) => value.rowSpacingMode !== 'fixed' || value.fixedClearGapM != null,
    { message: 'Enter a fixed clear row gap', path: ['fixedClearGapM'] }
  );

export const roofBearingWindowSchema = z.object({
  min: z.number().min(0).max(360),
  max: z.number().min(0).max(360),
});

/** Both caps clear the US form's maxima: 40 in setback, 20 in gap. */
export const solarPlacementRulesSchema = z.object({
  edgeSetbackM: z.number().min(0).max(1.2),
  panelGapM: z.number().min(0).max(0.6),
  /** Across columns (u). */
  columnGapM: z.number().min(0).max(0.6),
  /** Between successive rows (v); independent of module orientation. */
  rowGapM: z.number().min(0).max(0.6),
  excludeNorthFacing: z.boolean(),
  roofBearingWindowDeg: roofBearingWindowSchema,
  singleIsland: z.boolean(),
  singleOrientation: z.boolean(),
  quadrilateralArrays: z.boolean(),
  flatRoof: flatRoofSchema,
  minPanelsPerRoof: z.number().int().min(0).max(50),
  minSlopeDeg: z.number().min(0).max(80),
  minSunAccessPercent: z.number().min(0).max(100),
});

export type SolarPlacementRules = z.infer<typeof solarPlacementRulesSchema>;

export const defaultSolarPlacementRules = (
  region: SolarSavingsRegion = 'uk'
): SolarPlacementRules =>
  region === 'us'
    ? {
        ...DEFAULT_SOLAR_PLACEMENT_RULES,
        edgeSetbackM: US_DEFAULT_EDGE_SETBACK_M,
      }
    : { ...DEFAULT_SOLAR_PLACEMENT_RULES };

/**
 * Every field that validates is kept; only the ones that do not fall back to
 * the region default.
 */
export const parseSolarPlacementRules = (
  value: unknown,
  region: SolarSavingsRegion = 'uk'
): SolarPlacementRules => {
  const defaults = defaultSolarPlacementRules(region);
  const source = asRecord(value);
  const field = <K extends keyof SolarPlacementRules>(
    key: K
  ): SolarPlacementRules[K] => {
    const parsed = solarPlacementRulesSchema.shape[key].safeParse(source[key]);
    return parsed.success
      ? (parsed.data as SolarPlacementRules[K])
      : defaults[key];
  };
  const flatRoofSource = asRecord(source.flatRoof);
  const flatRoofField = <K extends keyof SolarPlacementRules['flatRoof']>(
    key: K
  ): SolarPlacementRules['flatRoof'][K] => {
    const parsed = flatRoofSchema.shape[key].safeParse(flatRoofSource[key]);
    return parsed.success
      ? (parsed.data as SolarPlacementRules['flatRoof'][K])
      : defaults.flatRoof[key];
  };
  const rowSpacingMode = flatRoofField('rowSpacingMode');
  const fixedClearGapM =
    flatRoofField('fixedClearGapM') ??
    (rowSpacingMode === 'fixed' ? 0 : undefined);
  return {
    edgeSetbackM: field('edgeSetbackM'),
    panelGapM: field('panelGapM'),
    columnGapM: field('columnGapM'),
    rowGapM: field('rowGapM'),
    excludeNorthFacing: field('excludeNorthFacing'),
    roofBearingWindowDeg: field('roofBearingWindowDeg'),
    singleIsland: field('singleIsland'),
    singleOrientation: field('singleOrientation'),
    quadrilateralArrays: field('quadrilateralArrays'),
    flatRoof: {
      maxSlopeDeg: flatRoofField('maxSlopeDeg'),
      frameTiltDeg: flatRoofField('frameTiltDeg'),
      rowSpacingMultiplier: flatRoofField('rowSpacingMultiplier'),
      rowSpacingMode,
      ...(fixedClearGapM !== undefined ? { fixedClearGapM } : {}),
    },
    minPanelsPerRoof: field('minPanelsPerRoof'),
    minSlopeDeg: field('minSlopeDeg'),
    minSunAccessPercent: field('minSunAccessPercent'),
  };
};

export const solarPanelGaps = (
  rules: Pick<SolarPlacementRules, 'columnGapM' | 'rowGapM'>
): { columnGapM: number; rowGapM: number } => ({
  columnGapM: rules.columnGapM,
  rowGapM: rules.rowGapM,
});

/** Fixed is a minimum, not an addition to the normal row gap. */
export const fixedSolarRowGap = (
  flatRoof: SolarPlacementRules['flatRoof'],
  rowGapM: number,
  onFrames: boolean
): number =>
  onFrames && flatRoof.rowSpacingMode === 'fixed'
    ? Math.max(rowGapM, flatRoof.fixedClearGapM ?? 0)
    : rowGapM;

/** Earth's axial tilt: the winter-solstice noon sun sits this far lower. */
const SOLSTICE_DECLINATION_DEG = 23.44;
/** Floor on the design sun so high-latitude rows stay finite. */
const MIN_DESIGN_SUN_ELEVATION_DEG = 5;
/** Matches the fixed clear-gap ceiling in the rules schema. */
export const MAX_TILT_FRAME_ROW_CLEARANCE_M = 10;

/** Winter-solstice noon sun elevation (°) at a latitude: 90 − |lat| − 23.44. */
export const designSunElevationDeg = (latitudeDegrees: number): number =>
  Math.min(
    90,
    Math.max(
      MIN_DESIGN_SUN_ELEVATION_DEG,
      90 - Math.abs(latitudeDegrees) - SOLSTICE_DECLINATION_DEG
    )
  );

/** What a packer can report when tilt frames on a roof face make no sense. */
export const FRAMES_NOT_ADVISED_NOTE = 'Frames not advised on this roof';

/**
 * The account set its own row-spacing multiplier: any value other than the
 * default rule's. The rules carry no separate "explicitly set" marker, so the
 * default value stands for "not set".
 */
export const isExplicitRowSpacingMultiplier = (multiplier: number): boolean =>
  multiplier !== DEFAULT_SOLAR_PLACEMENT_RULES.flatRoof.rowSpacingMultiplier;

export type TiltFrameRowSpacingArgs = {
  /** Module length up the tilted frame (m). */
  slopeLengthM: number;
  /** Absolute frame tilt above horizontal (°). */
  frameTiltDegrees: number;
  /** Physical roof pitch (°) and compass downslope (°). */
  roofPitchDegrees: number;
  roofAzimuthDegrees: number;
  /** Compass direction the modules face. */
  arrayAzimuthDegrees: number;
  /** Site latitude; omitted → the multiplier alone sets the design sun. */
  latitudeDegrees?: number | null;
  rowSpacingMultiplier: number;
};

/**
 * Calculated clear distance (m, measured on the ground along the array
 * azimuth) from the raised back edge of one tilt-frame row to the front edge
 * of the next, so the row behind is unshaded by the design sun, plus whether
 * frames make sense on this roof at all.
 *
 * The design sun stands in the array's facing direction at elevation α:
 * - an account that set its own `rowSpacingMultiplier` (any non-default
 *   value) gets atan(1 / multiplier) — an override, wider or narrower;
 * - otherwise the winter-solstice noon sun at `latitudeDegrees`;
 * - with neither, atan(1 / default multiplier) (1.5 ⇔ 33.7°).
 *
 * With roof pitch s and Δ the angle between the array azimuth and the roof's
 * downslope, the roof rises g = tan s · cos Δ per ground metre behind the
 * row (negative when it falls away from the sun). A module of slope length L
 * at absolute tilt β lifts its back edge H = L·sin β − L·cos β·g above the
 * roof beneath it, and the shadow of that edge meets the roof after
 *   d = H / (tan α + g).
 * On a flat roof this is L·sin β / tan α; a roof falling away from the sun
 * widens it, one rising toward it narrows it (never below 0).
 *
 * When tan α + g ≤ 0 the roof falls away faster than the sun rises: the
 * shadow never lands and no gap is shade-free. Then (and when d would pass
 * MAX_TILT_FRAME_ROW_CLEARANCE_M) the gap falls back to the multiplier rule
 * H × multiplier, always finite, and `framesNotAdvised` is set so a packer
 * can say so ({@link FRAMES_NOT_ADVISED_NOTE}).
 */
export const tiltFrameRowSpacing = (
  args: TiltFrameRowSpacingArgs
): { clearanceM: number; framesNotAdvised: boolean } => {
  const rad = Math.PI / 180;
  const beta = args.frameTiltDegrees * rad;
  const multiplier = args.rowSpacingMultiplier;
  const multiplierTan = multiplier > 0 ? 1 / multiplier : Infinity;
  const latitudeKnown =
    args.latitudeDegrees != null && Number.isFinite(args.latitudeDegrees);
  const tanSun =
    isExplicitRowSpacingMultiplier(multiplier) || !latitudeKnown
      ? multiplierTan
      : Math.tan(designSunElevationDeg(args.latitudeDegrees as number) * rad);
  if (!Number.isFinite(tanSun)) {
    return { clearanceM: 0, framesNotAdvised: false };
  }
  const rise =
    Math.tan(args.roofPitchDegrees * rad) *
    Math.cos((args.arrayAzimuthDegrees - args.roofAzimuthDegrees) * rad);
  const height =
    args.slopeLengthM * Math.sin(beta) -
    args.slopeLengthM * Math.cos(beta) * rise;
  if (!(height > 0)) return { clearanceM: 0, framesNotAdvised: false };
  const denominator = tanSun + rise;
  const clearance = denominator > 1e-9 ? height / denominator : Infinity;
  if (clearance <= MAX_TILT_FRAME_ROW_CLEARANCE_M) {
    return { clearanceM: clearance, framesNotAdvised: false };
  }
  const fallbackMultiplier =
    multiplier > 0
      ? multiplier
      : DEFAULT_SOLAR_PLACEMENT_RULES.flatRoof.rowSpacingMultiplier;
  return {
    clearanceM: Math.min(
      MAX_TILT_FRAME_ROW_CLEARANCE_M,
      height * fallbackMultiplier
    ),
    framesNotAdvised: true,
  };
};

/** The clear distance alone; see {@link tiltFrameRowSpacing}. */
export const tiltFrameRowClearanceM = (args: TiltFrameRowSpacingArgs): number =>
  tiltFrameRowSpacing(args).clearanceM;

/**
 * Verge/ridge = setback. UK eave and hip sit a little closer, as on MCS
 * layouts; a US layout keeps the full setback everywhere, because the number
 * is a fire-code clearance rather than a packing convention.
 */
export const insetsFromEdgeSetback = (
  edgeSetbackM: number,
  region: SolarSavingsRegion = 'uk'
): { boundaryInsetM: number; eaveInsetM: number; hipInsetM: number } =>
  region === 'us'
    ? {
        boundaryInsetM: edgeSetbackM,
        eaveInsetM: edgeSetbackM,
        hipInsetM: edgeSetbackM,
      }
    : {
        boundaryInsetM: edgeSetbackM,
        eaveInsetM: Math.max(0.05, edgeSetbackM - 0.15),
        hipInsetM: Math.max(0.05, edgeSetbackM - 0.2),
      };

/** Off / light / strict → sun-access floor. 0 is off. */
export const SUN_ACCESS_PRESETS = {
  off: 0,
  light: 70,
  strict: 85,
} as const;

export type SunAccessPreset = keyof typeof SUN_ACCESS_PRESETS;

export const sunAccessPresetFromPercent = (
  percent: number
): SunAccessPreset => {
  if (percent >= SUN_ACCESS_PRESETS.strict) return 'strict';
  if (percent >= SUN_ACCESS_PRESETS.light) return 'light';
  return 'off';
};

/** Unknown sun access (added faces, failed shading) is not skipped. */
export const facePassesSunAccess = (
  percent: number | null | undefined,
  minPercent: number
): boolean => {
  if (minPercent <= 0) return true;
  if (percent == null) return true;
  return percent >= minPercent;
};

export const MAX_PACKABLE_PITCH_DEGREES = 80;

/** Compass degrees clockwise from north. 360° is the same bearing as 0°. */
export const normaliseRoofBearing = (degrees: number): number =>
  ((degrees % 360) + 360) % 360;

/** Inclusive clockwise window; equal endpoints select one bearing, not all. */
export const roofBearingInWindow = (
  bearing: number,
  window: { min: number; max: number }
): boolean => {
  if (!Number.isFinite(bearing)) return false;
  const value = normaliseRoofBearing(bearing);
  const min = normaliseRoofBearing(window.min);
  const max = normaliseRoofBearing(window.max);
  return min > max
    ? value >= min || value <= max
    : value >= min && value <= max;
};

export const roofBearingWindowFromRules = (rules: SolarPlacementRules) =>
  rules.roofBearingWindowDeg;

/** Layout provenance only: this does not describe generation or panel rotation. */
export const roofBearingRuleKey = (rules: SolarPlacementRules): string => {
  if (!rules.excludeNorthFacing) return 'unrestricted';
  const window = roofBearingWindowFromRules(rules);
  return `${normaliseRoofBearing(window.min)}:${normaliseRoofBearing(window.max)}`;
};

/** Validate the canonical serialisation of a packed bearing restriction. */
export const isRoofBearingRuleKey = (key: string): boolean => {
  if (key === 'unrestricted') return true;
  const endpoints = key.split(':');
  return (
    endpoints.length === 2 &&
    endpoints.every((endpoint) => {
      const bearing = Number(endpoint);
      return (
        Number.isFinite(bearing) &&
        bearing >= 0 &&
        bearing < 360 &&
        String(bearing) === endpoint
      );
    })
  );
};

export const roofBearingRulesNeedRepack = (
  faceIds: number[],
  rules: SolarPlacementRules,
  applied: Record<string, string>
): boolean =>
  faceIds.some(
    (id) => applied[id] != null && applied[id] !== roofBearingRuleKey(rules)
  );

/** Capture missing legacy provenance before an edit, or overwrite after Repack. */
export const recordRoofBearingRules = (
  faceIds: number[],
  rules: SolarPlacementRules,
  applied: Record<string, string>,
  repacked = false
): Record<string, string> => ({
  ...applied,
  ...Object.fromEntries(
    faceIds.map((id) => [
      id,
      repacked
        ? roofBearingRuleKey(rules)
        : (applied[id] ?? roofBearingRuleKey(rules)),
    ])
  ),
});

/** YourPower's simple min/max filter cannot represent a window crossing north. */
export const providerRoofBearingWindow = (rules: SolarPlacementRules) => {
  if (!rules.excludeNorthFacing) return undefined;
  const window = roofBearingWindowFromRules(rules);
  const min = normaliseRoofBearing(window.min);
  const max = normaliseRoofBearing(window.max);
  return min < max ? { min, max } : undefined;
};

/** Outside east–south–west (90–270°). North-west and north-east count. */
export const isNorthFacingAzimuth = (azimuthDegrees: number): boolean => {
  const azimuth = ((azimuthDegrees % 360) + 360) % 360;
  return (
    azimuth < NORTH_FACING_AZIMUTH_WINDOW_DEG.min ||
    azimuth > NORTH_FACING_AZIMUTH_WINDOW_DEG.max
  );
};

export type PackSkipReason =
  | 'too-steep'
  | 'too-shallow'
  | 'north-facing'
  | 'poor-sun';

export const packSkipReason = (args: {
  pitchDegrees: number;
  azimuthDegrees?: number;
  sunAccessPercent?: number | null;
  rules: SolarPlacementRules;
  /** Explicit mounting takes precedence over the automatic threshold. */
  onFrames?: boolean;
}): PackSkipReason | null => {
  if (args.pitchDegrees > MAX_PACKABLE_PITCH_DEGREES) return 'too-steep';
  const onFrames =
    args.onFrames ?? args.pitchDegrees <= args.rules.flatRoof.maxSlopeDeg;
  if (
    !onFrames &&
    args.rules.minSlopeDeg > 0 &&
    args.pitchDegrees < args.rules.minSlopeDeg
  ) {
    return 'too-shallow';
  }
  if (
    args.rules.excludeNorthFacing &&
    args.azimuthDegrees != null &&
    !roofBearingInWindow(
      args.azimuthDegrees,
      roofBearingWindowFromRules(args.rules)
    )
  ) {
    return 'north-facing';
  }
  if (
    !facePassesSunAccess(args.sunAccessPercent, args.rules.minSunAccessPercent)
  ) {
    return 'poor-sun';
  }
  return null;
};
